import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  UserPlus,
  Mail,
  Trash2,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Users,
  Clock,
  Key,
  Eye,
  EyeOff,
  X,
  Shield,
  Sparkles,
  Unlock,
  Crown,
  GraduationCap,
  Puzzle,
  Ban,
  Search,
  Activity,
  MoonStar,
  UserX
} from 'lucide-react';
import { api } from '../../services/api';

const AdminUsers = ({ onAdminLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [emailLink, setEmailLink] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  const [showManualPassword, setShowManualPassword] = useState(false);
  const [creatingManual, setCreatingManual] = useState(false);
  
  const [resetModal, setResetModal] = useState({ open: false, user: null });
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingVipId, setTogglingVipId] = useState(null);
  const [togglingCourseId, setTogglingCourseId] = useState(null);
  const [togglingAIId, setTogglingAIId] = useState(null);
  const [togglingPluginId, setTogglingPluginId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // NOVO: Toggle IA
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [loadingMigrationPreview, setLoadingMigrationPreview] = useState(false);
  const [applyingMigration, setApplyingMigration] = useState(false);

  const [activeTab, setActiveTab] = useState('active'); // active | inactive | no_license | all
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // NOVA FUNÇÃO: Liberar acesso manual
  const handleToggleVip = async (userId) => {
    setTogglingVipId(userId);
    try {
      await api.toggleVipStatus(userId);
      setSuccess('Status VIP atualizado');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingVipId(null);
    }
  };

  // NOVA FUNÇÃO: Liberar acesso ao curso
  const handleToggleCourse = async (userId) => {
    setTogglingCourseId(userId);
    try {
      await api.toggleCourseStatus(userId);
      setSuccess('Acesso ao curso atualizado');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingCourseId(null);
    }
  };

  // NOVA FUNÇÃO: Toggle acesso à IA (chapéu mágico)
  const handleToggleAI = async (userId) => {
    setTogglingAIId(userId);
    try {
      await api.toggleAIStatus(userId);
      setSuccess('🤖 Acesso à IA atualizado');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingAIId(null);
    }
  };

  const handleCreateViaLink = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!emailLink.trim()) return;
    setCreatingLink(true);
    try {
      const result = await api.createUser(emailLink.trim());
      setSuccess(result.message);
      setEmailLink('');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCreateManual = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!manualEmail.trim() || !manualPassword) return;
    if (manualPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setCreatingManual(true);
    try {
      const result = await api.createUserManual(manualEmail.trim(), manualPassword);
      setSuccess(result.message);
      setManualEmail('');
      setManualPassword('');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingManual(false);
    }
  };

  const openResetModal = (user) => {
    setResetModal({ open: true, user });
    setResetPassword('');
    setShowResetPassword(false);
  };

  const closeResetModal = () => {
    setResetModal({ open: false, user: null });
    setResetPassword('');
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setResetting(true);
    setError(null);
    try {
      const result = await api.resetUserPassword(resetModal.user.id, resetPassword);
      setSuccess(result.message);
      closeResetModal();
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setResetting(false);
    }
  };

  const handleResendEmail = async (userId) => {
    setResendingId(userId);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.resendSetupEmail(userId);
      setSuccess(result.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendingId(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Remover acesso deste usuário?')) return;
    setDeletingId(userId);
    setError(null);
    try {
      await api.deleteUser(userId);
      setSuccess('Acesso removido');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePlugin = async (userId) => {
    setTogglingPluginId(userId);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${api.getApiUrl()}/api/admin/users/${userId}/toggle-plugin`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao alternar plugin');
      setSuccess('Acesso ao Plugin atualizado!');
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingPluginId(null);
    }
  };

  const handleRevokeAccess = async (userId) => {
    setRevokingId(userId);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${api.getApiUrl()}/api/admin/users/${userId}/revoke-access`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao revogar acesso');
      const data = await res.json();
      setSuccess(data.message || 'Todos os acessos revogados!');
      setConfirmRevoke(null);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const handlePreviewMigration = async () => {
    setLoadingMigrationPreview(true);
    setError(null);
    try {
      const preview = await api.previewAccessMigration(1000);
      setMigrationPreview(preview);
      setSuccess('Prévia da migração carregada');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMigrationPreview(false);
    }
  };

  const handleApplyMigration = async () => {
    if (!migrationPreview?.summary?.users_pending_backfill) {
      setError('Nenhum usuário pendente de migração');
      return;
    }
    if (!window.confirm(`Aplicar migração segura em ${migrationPreview.summary.users_pending_backfill} usuário(s)? Isso só adiciona compras/grants legados e não remove acessos atuais.`)) {
      return;
    }

    setApplyingMigration(true);
    setError(null);
    try {
      const result = await api.applyAccessMigration(1000);
      setMigrationPreview(result);
      setSuccess(`Migração aplicada em ${result.summary.users_backfilled} usuário(s)`);
      await loadUsers();
      const refreshedPreview = await api.previewAccessMigration(1000);
      setMigrationPreview(refreshedPreview);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingMigration(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getUserBucket = (user) => {
    if (user?.activity_status) return user.activity_status;
    const hasLicense = !!user?.has_license;
    if (!hasLicense) return 'no_license';
    const days = user?.days_since_last_activity;
    if (days === null || days === undefined) return 'never_logged_in';
    return days <= 30 ? 'active' : 'inactive';
  };

  const describeLastAccess = (user) => {
    const days = user?.days_since_last_activity;
    const last = user?.last_seen_at || user?.last_login_at;
    if (days === null || days === undefined || !last) {
      return { text: 'Nunca acessou', tone: 'text-white/30' };
    }
    let text;
    if (days === 0) text = 'Hoje';
    else if (days === 1) text = 'Ontem';
    else if (days < 30) text = `há ${days} dias`;
    else if (days < 60) text = 'há ~1 mês';
    else if (days < 365) text = `há ${Math.round(days / 30)} meses`;
    else text = `há ${Math.round(days / 365)} ano(s)`;
    let tone = 'text-emerald-400/70';
    if (days > 90) tone = 'text-red-400/80';
    else if (days > 30) tone = 'text-amber-400/80';
    return { text, tone };
  };

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.trim().toLowerCase();
    if (term && !(u.email || '').toLowerCase().includes(term)) return false;
    if (activeTab === 'all') return true;
    const bucket = getUserBucket(u);
    if (activeTab === 'active') return bucket === 'active';
    if (activeTab === 'inactive') return bucket === 'inactive' || bucket === 'never_logged_in';
    if (activeTab === 'no_license') return bucket === 'no_license';
    return true;
  });

  const counts = users.reduce(
    (acc, u) => {
      const bucket = getUserBucket(u);
      acc.all += 1;
      if (bucket === 'active') acc.active += 1;
      else if (bucket === 'inactive' || bucket === 'never_logged_in') acc.inactive += 1;
      else if (bucket === 'no_license') acc.no_license += 1;
      return acc;
    },
    { all: 0, active: 0, inactive: 0, no_license: 0 }
  );

  const tabs = [
    { key: 'active', label: 'Ativos', count: counts.active, icon: Activity, accent: 'emerald' },
    { key: 'inactive', label: 'Inativos', count: counts.inactive, icon: MoonStar, accent: 'amber' },
    { key: 'no_license', label: 'Sem licença', count: counts.no_license, icon: UserX, accent: 'red' },
    { key: 'all', label: 'Todos', count: counts.all, icon: Users, accent: 'white' },
  ];

  const accentClasses = (accent, isActive) => {
    const map = {
      emerald: isActive
        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        : 'text-white/50 hover:text-emerald-300 border-transparent',
      amber: isActive
        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        : 'text-white/50 hover:text-amber-300 border-transparent',
      red: isActive
        ? 'bg-red-500/15 text-red-300 border-red-500/30'
        : 'text-white/50 hover:text-red-300 border-transparent',
      white: isActive
        ? 'bg-white/10 text-white border-white/20'
        : 'text-white/50 hover:text-white border-transparent',
    };
    return map[accent] || map.white;
  };

  const getAccessBadges = (user) => {
    const resources = user?.access_summary?.resources || {};
    return Object.entries(resources)
      .filter(([, config]) => config?.active)
      .map(([key, config]) => ({
        key,
        label: config?.label || key,
        source: config?.sources?.[0]?.source || 'legacy',
      }));
  };

  const getPurchaseBadges = (user) => {
    return (user?.access_summary?.purchases || []).slice(0, 3);
  };

  const sourceLabel = (source) => {
    const labels = {
      purchase: 'Compra',
      manual: 'Manual',
      legacy: 'Legado',
      webhook: 'Webhook',
    };
    return labels[source] || source || 'N/D';
  };

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[20%] w-[500px] h-[500px] bg-[#FFCC00]/[0.02] rounded-full blur-[100px]" />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/[0.02] border-b border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin"
              className="w-10 h-10 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] flex items-center justify-center transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-white/50" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFCC00]/20 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#FFCC00]" />
              </div>
              <div>
                <h1 className="text-white font-medium tracking-tight">Gerenciar Acessos</h1>
                <p className="text-white/30 text-xs">Clientes e permissões</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-8">
        {error && (
          <div className="flex items-center gap-3 p-4 mb-6 backdrop-blur-xl bg-red-500/10 rounded-2xl border border-red-500/20">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="flex items-center gap-3 p-4 mb-6 backdrop-blur-xl bg-emerald-500/10 rounded-2xl border border-emerald-500/20">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <span className="text-emerald-300 text-sm">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFCC00]/15 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#FFCC00]" />
              </div>
              <div>
                <h2 className="text-white font-medium">Via Email</h2>
                <p className="text-white/30 text-xs">Cliente define a senha</p>
              </div>
            </div>
            
            <form onSubmit={handleCreateViaLink} className="space-y-4">
              <input
                type="email"
                value={emailLink}
                onChange={(e) => setEmailLink(e.target.value)}
                placeholder="cliente@email.com"
                required
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
              />
              <button
                type="submit"
                disabled={creatingLink || !emailLink.trim()}
                className="w-full px-4 py-3 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#FFCC00]/10"
              >
                {creatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                <span>{creatingLink ? 'Enviando...' : 'Enviar Link'}</span>
              </button>
            </form>
          </div>

          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-white font-medium">Manual</h2>
                <p className="text-white/30 text-xs">Admin define a senha</p>
              </div>
            </div>
            
            <form onSubmit={handleCreateManual} className="space-y-4">
              <input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                placeholder="cliente@email.com"
                required
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/30 focus:bg-white/[0.05] transition-all"
              />
              <div className="relative">
                <input
                  type={showManualPassword ? 'text' : 'password'}
                  value={manualPassword}
                  onChange={(e) => setManualPassword(e.target.value)}
                  placeholder="Senha (mín. 6 caracteres)"
                  required
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 pr-12 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/30 focus:bg-white/[0.05] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowManualPassword(!showManualPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showManualPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <button
                type="submit"
                disabled={creatingManual || !manualEmail.trim() || !manualPassword}
                className="w-full px-4 py-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {creatingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                <span>{creatingManual ? 'Criando...' : 'Criar Usuário'}</span>
              </button>
            </form>
          </div>
        </div>

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6 mb-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-white font-medium">Migração Segura de Acessos</h2>
              <p className="text-white/30 text-sm mt-1">
                Primeiro gere a prévia. A aplicação só cria compras e grants legados faltantes, sem apagar o modelo atual.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handlePreviewMigration}
                disabled={loadingMigrationPreview || applyingMigration}
                className="px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] text-white font-medium transition-all disabled:opacity-40 flex items-center gap-2"
              >
                {loadingMigrationPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span>{loadingMigrationPreview ? 'Analisando...' : 'Gerar Prévia'}</span>
              </button>
              <button
                onClick={handleApplyMigration}
                disabled={applyingMigration || !migrationPreview?.summary?.users_pending_backfill}
                className="px-4 py-3 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] font-medium transition-all disabled:opacity-40 flex items-center gap-2"
              >
                {applyingMigration ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                <span>{applyingMigration ? 'Migrando...' : 'Aplicar Migração'}</span>
              </button>
            </div>
          </div>

          {migrationPreview?.summary && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-white/30 text-[11px] uppercase tracking-[0.18em]">Escaneados</p>
                <p className="text-white text-xl mt-2">{migrationPreview.summary.users_scanned || 0}</p>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-white/30 text-[11px] uppercase tracking-[0.18em]">Legado</p>
                <p className="text-white text-xl mt-2">{migrationPreview.summary.users_with_legacy_access || 0}</p>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-white/30 text-[11px] uppercase tracking-[0.18em]">Pendentes</p>
                <p className="text-white text-xl mt-2">{migrationPreview.summary.users_pending_backfill || migrationPreview.summary.users_backfilled || 0}</p>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-white/30 text-[11px] uppercase tracking-[0.18em]">Compras</p>
                <p className="text-white text-xl mt-2">{migrationPreview.summary.purchase_backfills_needed || migrationPreview.summary.purchases_created || 0}</p>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-white/30 text-[11px] uppercase tracking-[0.18em]">Grants</p>
                <p className="text-white text-xl mt-2">{migrationPreview.summary.grant_backfills_needed || migrationPreview.summary.grants_created || 0}</p>
              </div>
            </div>
          )}

          {!!migrationPreview?.items?.length && (
            <div className="mt-5 rounded-2xl border border-white/[0.05] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.05] bg-white/[0.02]">
                <p className="text-white/70 text-sm">Amostra da migração</p>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {migrationPreview.items.slice(0, 8).map((item, index) => (
                  <div key={`${item.user_id || item.email || 'migration'}-${index}`} className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-white text-sm font-medium">{item.email}</span>
                        {item.inferred_product_name && (
                          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-[#0056D2]/10 border border-[#0056D2]/20 text-[#8CB8FF]">
                            {item.inferred_product_name}
                          </span>
                        )}
                        {item.status && (
                          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/60">
                            {item.status}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(item.missing_resources || item.created_resources || []).map((resource) => (
                          <span
                            key={`${item.email}-${resource}`}
                            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/60"
                          >
                            {resource}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] overflow-hidden">
          <div className="px-6 py-5 border-b border-white/[0.05] flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#FFCC00]/60" />
              <h2 className="text-white font-medium">Clientes ({counts.all})</h2>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.key;
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-3 py-2 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${accentClasses(tab.accent, isActive)}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-black/30' : 'bg-white/[0.06]'}`}>
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="relative lg:ml-auto lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por email..."
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-all"
                />
              </div>
            </div>
          </div>

          <div className="divide-y divide-white/[0.03]">
            {loading ? (
              <div className="p-16 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00] mx-auto mb-3" />
                <p className="text-white/30 text-sm">Carregando...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-16 text-center">
                <p className="text-white/30 text-sm">Nenhum usuário nesta categoria.</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.id} className="group flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${
                      user.access_override 
                        ? 'bg-[#FFCC00]/20 border-[#FFCC00]/30 shadow-[0_0_15px_rgba(255,204,0,0.1)]' 
                        : (user.has_set_password ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-amber-500/10 border border-amber-500/20')
                    }`}>
                      {user.access_override ? (
                        <Crown className="w-5 h-5 text-[#FFCC00]" />
                      ) : user.has_set_password ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.email}</p>
                      <p className="text-white/30 text-xs">
                        {user.has_set_password ? 'Senha definida' : 'Pendente'} • Criado em {formatDate(user.created_at)}
                      </p>
                      {(() => {
                        const last = describeLastAccess(user);
                        const bucket = getUserBucket(user);
                        const bucketLabel = {
                          active: 'Ativo',
                          inactive: 'Inativo',
                          never_logged_in: 'Nunca acessou',
                          no_license: 'Sem licença',
                        }[bucket] || bucket;
                        const bucketTone = {
                          active: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
                          inactive: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
                          never_logged_in: 'bg-white/[0.04] text-white/50 border-white/10',
                          no_license: 'bg-red-500/10 text-red-300 border-red-500/20',
                        }[bucket] || 'bg-white/[0.04] text-white/50 border-white/10';
                        return (
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${bucketTone}`}>
                              {bucketLabel}
                            </span>
                            <span className={`text-xs ${last.tone}`}>
                              Último acesso: {last.text}
                            </span>
                          </div>
                        );
                      })()}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {getPurchaseBadges(user).map((purchase, index) => (
                          <span
                            key={`${user.id}-purchase-${index}`}
                            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-[#0056D2]/10 border border-[#0056D2]/20 text-[#8CB8FF]"
                          >
                            {purchase.product_name}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {getAccessBadges(user).map((access) => (
                          <span
                            key={`${user.id}-access-${access.key}`}
                            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/60"
                            title={`Origem: ${sourceLabel(access.source)}`}
                          >
                            {access.label} • {sourceLabel(access.source)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    {/* BOTÃO VIP */}
                    <button
                      onClick={() => handleToggleVip(user.id)}
                      className={`p-2.5 rounded-xl border transition-all ${
                        user.access_override 
                          ? 'bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30' 
                          : 'hover:bg-white/[0.05] text-white/40 hover:text-white border-transparent hover:border-white/10'
                      }`}
                      title={user.access_override ? "Remover VIP" : "Liberar VIP"}
                    >
                      {togglingVipId === user.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Unlock size={18} />
                      )}
                    </button>

                    {/* BOTÃO CURSO */}
                    <button
                      onClick={() => handleToggleCourse(user.id)}
                      className={`p-2.5 rounded-xl border transition-all ${
                        user.has_course || user.course_override
                          ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                          : 'hover:bg-white/[0.05] text-white/40 hover:text-white border-transparent hover:border-white/10'
                      }`}
                      title={user.has_course || user.course_override ? "Remover acesso ao curso" : "Liberar curso"}
                    >
                      {togglingCourseId === user.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <GraduationCap size={18} />
                      )}
                    </button>

                    {/* BOTÃO IA MATCHING */}
                    <button
                      onClick={() => handleToggleAI(user.id)}
                      className={`p-2.5 rounded-xl border transition-all ${
                        user.has_ai_access
                          ? 'bg-purple-500/15 text-purple-400 border-purple-500/30'
                          : 'hover:bg-white/[0.05] text-white/40 hover:text-white border-transparent hover:border-white/10'
                      }`}
                      title={user.has_ai_access ? "Remover acesso à IA" : "Liberar IA (chapéu mágico)"}
                    >
                      {togglingAIId === user.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Sparkles size={18} />
                      )}
                    </button>

                    {/* BOTÃO PLUGIN */}
                    <button
                      onClick={() => handleTogglePlugin(user.id)}
                      className={`p-2.5 rounded-xl border transition-all ${
                        user.has_plugin
                          ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                          : 'hover:bg-white/[0.05] text-white/40 hover:text-white border-transparent hover:border-white/10'
                      }`}
                      title={user.has_plugin ? "Remover acesso ao Plugin" : "Liberar Plugin (plano 87+)"}
                    >
                      {togglingPluginId === user.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Puzzle size={18} />
                      )}
                    </button>

                    {/* BOTÃO RESETAR SENHA */}
                    {user.has_set_password && (
                      <button
                        onClick={() => openResetModal(user)}
                        className="p-2.5 rounded-xl border border-transparent hover:border-white/10 hover:bg-white/[0.05] text-white/40 hover:text-white transition-all"
                        title="Resetar senha"
                      >
                        <Key size={18} />
                      </button>
                    )}

                    {/* BOTÃO REENVIAR EMAIL */}
                    {!user.has_set_password && (
                      <button
                        onClick={() => handleResendEmail(user.id)}
                        disabled={resendingId === user.id}
                        className="p-2.5 rounded-xl border border-transparent hover:border-white/10 hover:bg-white/[0.05] text-white/40 hover:text-white transition-all disabled:opacity-40"
                        title="Reenviar email"
                      >
                        {resendingId === user.id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <RefreshCw size={18} />
                        )}
                      </button>
                    )}

                    {/* BOTÃO REVOGAR ACESSO (REEMBOLSO) */}
                    {confirmRevoke === user.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRevokeAccess(user.id)}
                          disabled={revokingId === user.id}
                          className="px-2 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-all flex items-center gap-1"
                          title="Confirmar revogação"
                        >
                          {revokingId === user.id
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Ban size={14} />
                          }
                          Confirmar
                        </button>
                        <button
                          onClick={() => setConfirmRevoke(null)}
                          className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 transition-all"
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRevoke(user.id)}
                        className="p-2.5 rounded-xl border border-transparent hover:border-red-500/30 hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-all"
                        title="Revogar TODOS os acessos (reembolso)"
                      >
                        <Ban size={18} />
                      </button>
                    )}

                    {/* BOTÃO DELETAR USUÁRIO */}
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={deletingId === user.id}
                      className="p-2.5 rounded-xl border border-transparent hover:border-red-500/20 hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-all disabled:opacity-40"
                      title="Remover usuário"
                    >
                      {deletingId === user.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* MODAL DE RESET DE SENHA */}
      {resetModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-md backdrop-blur-xl bg-white/[0.03] border border-white/[0.08] rounded-3xl p-8">
            <button
              onClick={closeResetModal}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-white/[0.05] flex items-center justify-center transition-all"
            >
              <X className="w-5 h-5 text-white/50" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <Key className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Resetar Senha</h3>
                <p className="text-white/40 text-sm">{resetModal.user?.email}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <input
                  type={showResetPassword ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Nova senha (mín. 6 caracteres)"
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 pr-12 text-white placeholder-white/30 focus:outline-none focus:border-amber-500/30 focus:bg-white/[0.05] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showResetPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={closeResetModal}
                  className="flex-1 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] text-white font-medium transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting || !resetPassword || resetPassword.length < 6}
                  className="flex-1 px-4 py-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/25 text-amber-400 font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {resetting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Resetando...</span>
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminUsers;

